import { styles, useBreakpoints, useCustomRouteMatch, usePendingChangesContext } from "@repo/helpers";
import { CircleDot, Menu } from "lucide-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../basic/button";
import { Tabs, TabsList, TabsTrigger } from "../basic/tabs";
import { useAlertContext } from "../providers/alert";
import style from "./style.module.scss";
import { LayoutPage, LayoutState } from "./types";

interface LayoutProps {
	title: ReactNode;

	showSidebar: boolean;
	defaultActiveState: string;
	defaultPage: string;

	pages: Record<string, LayoutPage[]>;
	states: LayoutState[];
	entityId?: string;
	pendingChanges?: boolean;

	sidebarAction?: ReactNode;
	headerAction?: ReactNode;
	children: ReactNode;
}

export const PortalLayout: FC<LayoutProps> = ({
	title,
	headerAction,
	showSidebar,
	defaultActiveState,
	defaultPage,
	pages,
	states,
	entityId,
	pendingChanges,
	sidebarAction,
	children,
}) => {
	const [activeState, setActiveState] = useState(defaultActiveState);
	const [activePage, setActivePage] = useState(defaultPage);
	const [searchOpen, setSearchOpen] = useState(false);
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const { url: initUrl } = useCustomRouteMatch();
	const { isTabletDown } = useBreakpoints();
	const history = useHistory();
	const { clearAlert, unsavedChangesAlert } = useAlertContext();
	const { clearAllChanges } = usePendingChangesContext();
	const isInModal = true;

	const isSidebarOpen = showSidebar && sidebarOpen;

	// Auto-close sidebar on mobile
	useEffect(() => {
		if (isTabletDown) {
			setSidebarOpen(false);
		} else {
			setSidebarOpen(true);
		}
	}, [isTabletDown]);

	useEffect(() => {
		if (!states) return;
		if (states.length === 1) {
			setActiveState(states?.[0].id);
		}
	}, [states]);

	// Handle keyboard shortcut
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "k") {
				e.preventDefault();
				setSearchOpen(true);
			}
			if (e.key === "Escape" && searchOpen) {
				setSearchOpen(false);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [searchOpen]);

	// Get current state colors
	const currentState = states?.find((i) => i.id === activeState);

	let url = initUrl
		.split("/")
		.filter((o) => o || o === "0")
		.slice(0, 2)
		.join("/");
	if (url) url = "/" + url;

	const { pathname } = history.location;

	useEffect(() => {
		const parts = pathname.split("/");
		const pageId = parts[parts.length - 1];
		if (pageId && pages[activeState]?.find((p) => p.id === pageId)) {
			setActivePage(pageId);
		}
	}, [pathname, activeState, pages]);

	useEffect(() => {
		if (activePage !== defaultPage && !pathname.includes(activePage)) setActivePage(defaultPage);
	}, [defaultPage]);

	return (
		<div className={isInModal ? style.flexContainerModal : style.flexContainer}>
			<header className={isInModal ? style.headerModal : style.header}>
				<div className={style.headerContent}>
					<div className={style.headerLeft}>
						{isTabletDown && showSidebar && (
							<Button className={style.marginRight2} onClick={() => setSidebarOpen(!sidebarOpen)}>
								<Menu className={style.menuIcon} />
								<span className={style.srOnly}>Toggle menu</span>
							</Button>
						)}
						{title}
					</div>
					{headerAction}
				</div>
			</header>
			<div
				className={styles(
					isInModal ? style.sidebarModal : style.sidebar,
					isSidebarOpen ? style.sidebarOpen : style.sidebarClosed,
					style.sidebarLg,
				)}
				hidden={!showSidebar}
				style={
					isInModal
						? { top: "56px", height: "calc(100% - 56px)" }
						: { top: "56px", height: "calc(100vh - 56px)" }
				}
			>
				<div className={style.sidebarContent}>
					{states.length > 1 && (
						<div className={style.sidebarSection}>
							<Tabs
								value={activeState}
								onValueChange={setActiveState}
								className={style.fullWidth}
								orientation="vertical"
							>
								<TabsList className={style.tabsList}>
									{states.map((state) => (
										<TabsTrigger key={state.id} value={state.id}>
											<span>{state.label}</span>
										</TabsTrigger>
									))}
								</TabsList>
							</Tabs>
						</div>
					)}
					<div className={style.sidebarNav}>
						<nav className={style.sidebarNavList}>
							{sidebarAction}
							{pages[activeState].map((page) => (
								<button
									key={page.id}
									onClick={() => {
										const onConfirm = () => {
											setActivePage(page.id);
											history.push(url + "/" + entityId + "/" + page.id);
											clearAllChanges();
										};

										if (activePage !== page.id && pendingChanges) {
											unsavedChangesAlert(onConfirm);
										} else {
											onConfirm();
										}
									}}
									className={styles(
										style.navButton,
										page.disabled && style.disabled,
										activePage === page.id
											? currentState?.activeColor + " " + currentState?.activeTextColor
											: style.inactive,
									)}
									disabled={page.disabled}
									hidden={page.hidden}
								>
									<div className={style.navButtonContent}>
										<div className={style.navButtonLabel}>
											<page.icon className={style.navButtonIcon} />
											{page.label}
											{page.count ? ` (${page.count}) ` : " "}
										</div>
										{page.changes ? <CircleDot className={style.pendingChangesIcon} /> : null}
									</div>
								</button>
							))}
						</nav>
					</div>
				</div>
			</div>
			{isSidebarOpen && isTabletDown && (
				<div className={styles(style.overlay, style.overlayLg)} onClick={() => setSidebarOpen(false)} />
			)}
			<main
				className={styles(
					style.mainContent,
					isSidebarOpen ? style.mainContentWithSidebar : style.mainContentWithoutSidebar,
				)}
				style={{ marginTop: "56px" }}
			>
				{children}
			</main>
		</div>
	);
};

export * from "./types";
